// @wavvest/shared-services — Sentry initialization (parameterized)
// Extracted from: apps/*/src/sentry.ts
// Both portals have near-identical Sentry setup. The only differences:
//   - DSN, release name, and forceSentry flag are app-specific → passed as config
//   - import.meta.env.PROD is Vite-specific → passed as isProd config param

import * as Sentry from "@sentry/react";

export interface SentryConfig {
  dsn: string;
  isProd: boolean;
  forceSentry?: boolean;
  release: string;
  environment?: string;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
  ignoreErrors?: string[];
}

export function initSentry(config: SentryConfig): void {
  if (!config.dsn) {
    console.warn("Sentry DSN not configured, error tracking disabled");
    return;
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment ?? (config.isProd ? "prod" : "stg"),

    // Performance Monitoring — disabled in non-prod unless forced
    tracesSampleRate:
      config.tracesSampleRate ?? (config.isProd || config.forceSentry ? 0.1 : 0),

    // Session Replay — disabled in non-prod unless forced
    replaysSessionSampleRate:
      config.replaysSessionSampleRate ?? (config.isProd || config.forceSentry ? 0.1 : 0),
    replaysOnErrorSampleRate:
      config.replaysOnErrorSampleRate ?? (config.isProd || config.forceSentry ? 1.0 : 0),

    // Release version
    release: config.release,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],

    // Errors to ignore
    ignoreErrors: config.ignoreErrors ?? [
      "Network request failed",
      "Failed to fetch",
      "AbortError",
      "ResizeObserver loop limit exceeded",
    ],

    // Don't send events in development unless forced
    beforeSend(event) {
      if (!config.isProd && !config.forceSentry) {
        return null;
      }
      return event;
    },
  });
}

// Set user context when authenticated
export function setSentryUser(user: {
  id?: string;
  username?: string;
}): void {
  Sentry.setUser({
    id: user.id,
    username: user.username,
  });
}

// Clear user context on logout
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

// Add custom tags
export function setSentryTag(key: string, value: string): void {
  Sentry.setTag(key, value);
}

// Capture exceptions with context
export function captureException(
  error: Error,
  context?: Record<string, unknown>,
): void {
  Sentry.captureException(error, {
    extra: context,
  });
}

// Capture messages
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
): void {
  Sentry.captureMessage(message, level);
}

// Re-export ErrorBoundary for use in components
export const SentryErrorBoundary = Sentry.ErrorBoundary;
